import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
    resetTwoFactorAuth,
    resetTwoFactorAuthRecoveryCodes,
    resetTwoFactorAuthRecoveryCodesFailure,
    resetTwoFactorAuthRecoveryCodesSuccess,
    setupTwoFactorAuth,
    setupTwoFactorAuthFailure,
    setupTwoFactorAuthSuccess,
    verifyTwoFactorAuth,
    verifyTwoFactorAuthFailure,
    verifyTwoFactorAuthSuccess,
} from './two-factor-auth-setup.actions';
import { catchError, exhaustMap, map, switchMap } from 'rxjs/operators';
import { AuthenticationApiService } from '../../services/authentication-api.service';
import { of } from 'rxjs';
import { twoFactorAuthError } from './two-factor-auth-error';

@Injectable({
    providedIn: 'root',
})
export class TwoFactorAuthSetupEffects {
    setupTwoFactorAuth$ = createEffect(() =>
        this.actions$.pipe(
            ofType(setupTwoFactorAuth),
            switchMap(({ is2FAEnabled, currentPassword }) =>
                this.authService.setup2FA(is2FAEnabled, currentPassword).pipe(
                    map((setup) => setupTwoFactorAuthSuccess({ setup })),
                    catchError((error) =>
                        of(
                            setupTwoFactorAuthFailure({
                                error: twoFactorAuthError(error),
                            })
                        )
                    )
                )
            )
        )
    );

    onResetTwoFactorAuth$ = createEffect(() =>
        this.actions$.pipe(
            ofType(resetTwoFactorAuth),
            switchMap(({ currentPassword }) =>
                this.authService.setup2FA(false, currentPassword).pipe(
                    // 2FA is off from here, whatever the new key's call does, so the page says so first
                    switchMap(() => [setupTwoFactorAuthSuccess({ setup: null }), setupTwoFactorAuth({ is2FAEnabled: true, currentPassword })]),
                    catchError((error) =>
                        of(
                            setupTwoFactorAuthFailure({
                                error: twoFactorAuthError(error),
                            })
                        )
                    )
                )
            )
        )
    );

    verifyTwoFactorAuth$ = createEffect(() =>
        this.actions$.pipe(
            ofType(verifyTwoFactorAuth),
            // One code at a time: a second, sent as the first turns 2FA on, would be refused and hide the codes
            exhaustMap(({ otp, currentPassword }) =>
                this.authService.verify2FA(otp, currentPassword).pipe(
                    map((response) =>
                        verifyTwoFactorAuthSuccess({ recoveryCodes: response })
                    ),
                    catchError((error) =>
                        of(
                            verifyTwoFactorAuthFailure({
                                error: twoFactorAuthError(error),
                            })
                        )
                    )
                )
            )
        )
    );

    resetTwoFactorAuthRecoveryCodes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(resetTwoFactorAuthRecoveryCodes),
            switchMap(({ currentPassword }) =>
                this.authService.reset2FARecoveryCodes(currentPassword).pipe(
                    map((response) =>
                        resetTwoFactorAuthRecoveryCodesSuccess({
                            recoveryCodes: response,
                        })
                    ),
                    catchError((error) =>
                        of(
                            resetTwoFactorAuthRecoveryCodesFailure({
                                error: twoFactorAuthError(error),
                            })
                        )
                    )
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private authService: AuthenticationApiService
    ) {}
}
